import { type ServerService } from 'src/lib/services/server-data'

import { fetchUserWithToken } from '@lib/services/user/client-fallback'
import { User } from '@lib/services/user/user'

enum PayloadState {
  Empty = 'empty',
  Success = 'success',
  Pending = 'pending',
  Error = 'error',
}

interface Payload<T> {
  state: PayloadState
  promise?: Promise<T>
}

const emptyPayload: Payload<User> = { state: PayloadState.Empty }

export class UserService {
  currentUser: User
  private userPayload: Payload<User> = emptyPayload

  constructor(shWebServerService: ServerService) {
    const { user: userData } = shWebServerService.get()
    this.currentUser = User.create(userData)
  }

  async initialLoad(): Promise<User> {
    if (!this.currentUser.isNone) {
      return this.currentUser
    }

    // If User is None, fetch user data from mediator API following the same flow as proxy server:
    // 1. Get token from /v1/mediator/token
    // 2. Get user info from /auth/v5/api/userinfo with Bearer token
    // This happens when hitting super-funnel directly without server-side user data
    if (this.userPayload.state !== PayloadState.Pending) {
      this.userPayload = {
        state: PayloadState.Pending,
        promise: fetchUserWithToken().then(userData => User.create(userData)),
      }
    }

    try {
      this.currentUser = await this.userPayload.promise!

      this.userPayload = { state: PayloadState.Success }

      return this.currentUser
    } catch {
      this.userPayload = { state: PayloadState.Error }
      // On error, treat as anonymous user
      this.currentUser = User.None
      return this.currentUser
    }
  }
}
